import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import { parse } from 'tldts';

countries.registerLocale(en);

export const ESTIMATE_SUBSCRIPTION_FEE = 13.99;
export const DEFAULT_TERRITORY = 'Unknown';
export const DEFAULT_PROVIDER = 'Unknown ISP';
export const nonTrafficDomains = [
  'blogspot.com', 'facebook.com', 'twitter.com', 'instagram.com', 'linkedin.com',
  'youtube.com', 'vimeo.com', 'dailymotion.com', 'reddit.com', 'pinterest.com',
  'tumblr.com', 'flickr.com', 'telegram.org', 't.me', 'vk.com', 'x.com',
];
// Constants used for tabs, panels, and tables
export function getLiveTrackerConstants() {
  return {
    EVENT_TABS: [
      { key: 'pre-event', label: 'PRE-EVENT' },
      { key: 'event-day', label: 'EVENT DAY' },
      { key: 'post-event', label: 'POST-EVENT' },
      { key: 'management-report', label: 'EXECUTIVE REPORT' },
    ],
    LOCKED_PANELS: [
      'CDN and Hosting Info',
      'Takedown Notices Overview',
      'Search Engine Delistings Overview',
    ],
    DOMAIN_HEADER_MAP: [
      { uid: 'domain', display: 'Domain', size: 'auto' },
      { uid: 'count', display: 'Results', size: 'auto' },
      {
        uid: 'firstDetectedAt',
        display: 'First Detected',
        size: 'auto',
        eventDayOnly: true,
      },
      { uid: 'ipAddress', display: 'IP Address', size: 'auto' },
      { uid: 'ipGeolocation', display: 'IP Geolocation', size: 'auto' },
      { uid: 'ipTimestamp', display: 'IP Timestamp', size: 'auto' },
      { uid: 'ispName', display: 'IP ISP', size: 'auto' },
    ],
    TOP_SITES_HEADER_MAP: [
      { uid: 'domain', display: 'Domain', size: 'auto' },
      { uid: 'monthlyVisitors', display: 'Unique Visitors', size: 'auto' },
      { uid: 'globalRanking', display: 'Global Rank', size: 'auto' },
    ],
  };
}

// --- String/Domain Helpers ---
export function normalizeDomain(value = '') {
  return String(value || '').toLowerCase().replace(/^www\./, '').trim();
}

export function shortLabel(value = '', max = 20) {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

export function extractRootDomain(domain = '') {
  const result = parse(domain);
  return result.domainWithoutSuffix || '';
}

// --- Formatting ---
export function formatInt(value) {
  if (typeof value === 'string') return value;
  return Number(value || 0).toLocaleString('en-US');
}

export function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatDateTime(value) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function getMetricDescription(label = '', useSubscriptionFee = ESTIMATE_SUBSCRIPTION_FEE) {
  const text = String(label).toLowerCase();
  if (text.includes('total events')) return 'Count of events included in this reporting scope';
  if (text.includes('total infringing links')) return 'Total infringement records detected for the selected period';
  if (text.includes('unique links')) return 'Distinct infringing URLs after deduplication';
  if (text.includes('total unique domains')) return 'Distinct domains hosting infringements';
  if (text.includes('total unique root domains')) return 'Distinct root domains hosting infringements';
  if (text.includes('urls found on google')) return 'Detected infringing URLs discoverable via Google';
  if (text.includes('urls found on bing')) return 'Detected infringing URLs discoverable via Bing';
  if (text.includes('total viewers')) return 'Modeled piracy audience derived from host traffic data';
  if (text.includes('estimated loss of revenue')) {
    return (
      `Estimate uses ${formatCurrency(useSubscriptionFee)} subscription tier`
    );
  }
  if (text.includes('total watermark detections')) return 'Count of infringing-links/files with a source detected in watermarks';
  if (text.includes('avg est. viewers / link')) return 'Estimated viewers per link using site-level traffic assumptions';
  if (text.includes('total est. viewers')) return 'Lower/upper audience estimate based on sensitivity bounds';
  if (text.includes('revenue at risk')) return `Sensitivity-adjusted estimate using ${formatCurrency(useSubscriptionFee)} subscription tier`;
  if (text.includes('mirror domains')) return 'Count of mirror/sub-domain hosts tied to infringing activity';
  if (text.includes('root domains')) return 'Count of unique root domains tied to infringing activity';
  if (text.includes('stream resurfacing rate')) return 'Share of domains reappearing under mirror URLs';
  if (text.includes('aggregate concurrent estimate')) return 'Estimated concurrent viewers across active domains';
  if (text.includes('unique pirate viewer estimate')) return 'Modeled unique piracy viewers with turnover adjustment';
  if (text.includes('modeled total event audience')) return 'Expanded modeled audience for the full event window';
  if (text.includes('avg time to detection')) return 'Modeled estimate from first-seen patterns; not exact start time';
  if (text.includes('spike vs pre')) return 'Percent increase/decrease from Pre-Event baseline to Event Day';
  if (text.includes('pre + event')) return 'Combined total from Pre-Event and Event Day data';
  if (text.includes('traffic chokepoint')) return 'Share of traffic concentrated in top infrastructure providers';
  if (text.includes('revenue loss range')) return 'Total loss range using post-event lower/upper sensitivity bounds';
  return 'Modeled metric derived from infringement and host intelligence data';
}
// --- Country/Geo ---
export function getCountryCodeFromLocation(location = '') {
  const normalized = String(location || '').trim();
  if (!normalized) return null;
  const code = countries.getAlpha2Code(normalized, 'en');
  return code || null;
}

// --- Date Parsing ---
export function toDateOnlyTimestamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function toDateTimeTimestamp(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.getTime();
}

export function parseDateValue(value) {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    return toDateOnlyTimestamp(value);
  }
  if (typeof value === 'object') {
    const candidateKeys = [
      'date', 'datetime', 'timestamp', 'created_at', 'createdAt',
      'found_date', 'foundDate', 'value', 'label',
    ];
    for (let i = 0; i < candidateKeys.length; i += 1) {
      const parsed = toDateOnlyTimestamp(value[candidateKeys[i]]);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

export function parseDateTimeValue(value) {
  if (!value) return null;
  if (typeof value === 'string' || typeof value === 'number' || value instanceof Date) {
    return toDateTimeTimestamp(value);
  }
  if (typeof value === 'object') {
    const candidateKeys = [
      'date', 'datetime', 'timestamp', 'created_at', 'createdAt',
      'found_date', 'foundDate', 'value', 'label',
    ];
    for (let i = 0; i < candidateKeys.length; i += 1) {
      const parsed = toDateTimeTimestamp(value[candidateKeys[i]]);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

export function displayOrUnavailable(value, formatter = null) {
  // If value is null or undefined, treat as unavailable
  if (value === 0 || value === '0' || value === null || value === undefined) {
    return 'Data unavailable';
  }
  return formatter ? formatter(value) : value;
}

// --- Row/Cell Extraction ---
export function extractPrimaryUrl(row = []) {
  for (let i = 0; i < row.length; i += 1) {
    const cell = row[i];
    if (cell?.display && typeof cell.display === 'object' && cell.display.url) return cell.display.url;
    if (cell?.decoration === 'url' && typeof cell?.display === 'string') return cell.display;
    if (typeof cell?.display === 'string' && cell.display.startsWith('http')) return cell.display;
  }
  return null;
}

export function getDomainFromUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return normalizeDomain(parsed.hostname);
  } catch (e) {
    return null;
  }
}

export function getUniqueDomainsFromRows(rows = []) {
  const domains = new Set();
  rows.forEach((row) => {
    const url = extractPrimaryUrl(row);
    if (!url) return;
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      if (hostname) domains.add(hostname);
      const normalized = normalizeDomain(hostname);
      if (normalized && normalized !== hostname) domains.add(normalized);
    } catch (e) {
      // Fail silently
    }
  });
  return Array.from(domains);
}

export function getFoundDateFromRow(row = [], headers = []) {
  if (!Array.isArray(row) || row.length === 0) return null;
  const foundDateIndex = headers.findIndex((header = {}) => {
    const label = String(header.display || header.uid || '').toLowerCase();
    return (
      label.includes('found date')
      || label === 'found'
      || label.includes('detected')
    );
  });
  if (foundDateIndex >= 0 && row[foundDateIndex]) {
    const cell = row[foundDateIndex];
    const parsed = parseDateValue(cell?.display ?? cell?.value ?? cell?.api);
    if (parsed !== null) return parsed;
  }
  for (let i = 0; i < row.length; i += 1) {
    const cell = row[i];
    if (cell?.decoration === 'datetime') {
      const parsed = parseDateValue(cell?.display ?? cell?.value ?? cell?.api);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

export function getFoundDateTimeFromRow(row = [], headers = []) {
  if (!Array.isArray(row) || row.length === 0) return null;
  const foundDateIndex = headers.findIndex((header = {}) => {
    const label = String(header.display || header.uid || '').toLowerCase();
    return (
      label.includes('found date')
      || label === 'found'
      || label.includes('detected')
    );
  });
  if (foundDateIndex >= 0 && row[foundDateIndex]) {
    const cell = row[foundDateIndex];
    const parsed = parseDateTimeValue(cell?.display ?? cell?.value ?? cell?.api);
    if (parsed !== null) return parsed;
  }
  for (let i = 0; i < row.length; i += 1) {
    const cell = row[i];
    if (cell?.decoration === 'datetime') {
      const parsed = parseDateTimeValue(cell?.display ?? cell?.value ?? cell?.api);
      if (parsed !== null) return parsed;
    }
  }
  return null;
}

export function getEventTitleFromRow(row = [], headers = []) {
  const titleIdx = headers.findIndex(
    (header) => String(header.display || header.uid || '').toLowerCase().includes('title'),
  );
  if (titleIdx !== -1 && row[titleIdx]?.display) {
    return row[titleIdx].display;
  }
  return 'Unknown Event';
}

export function getMatchdayFromRow(row = [], headers = []) {
  const matchdayIdx = headers.findIndex(
    (header) => String(header.display || header.uid || '').toLowerCase().includes('match day'),
  );
  if (matchdayIdx !== -1 && row[matchdayIdx]?.display) {
    return row[matchdayIdx].display;
  }
  return 'Unknown Match Day';
}

// --- Event Datetime Extraction ---
export function extractEventDatetimeFromHeader(infringementsData = null, reviewWidget = null) {
  const topLevelHeader = Array.isArray(infringementsData?.header) ? infringementsData.header : [];
  const reviewHeader = Array.isArray(reviewWidget?.value?.header) ? reviewWidget.value.header : [];
  const dataWidgets = Array.isArray(infringementsData?.data) ? infringementsData.data : [];
  const findEventValue = (items = []) => {
    let eventValue = null;
    const headerIndex = items?.[0]?.value?.header?.findIndex((header) => header?.display?.toLowerCase().includes('event datetime')) ?? -1;
    if (headerIndex >= 0) {
      const firstRow = items?.[0]?.value?.rows?.[0] ?? [];
      const cell = firstRow[headerIndex];
      const parsed = parseDateValue(cell?.display ?? cell?.value ?? cell?.api);
      if (parsed !== null) eventValue = parsed;
    }
    return eventValue;
  };

  const directCandidates = [
    findEventValue(topLevelHeader),
    findEventValue(reviewHeader),
    findEventValue(dataWidgets),
  ];

  for (let i = 0; i < directCandidates.length; i += 1) {
    if (directCandidates[i] !== null) return directCandidates[i];
  }

  for (let i = 0; i < dataWidgets.length; i += 1) {
    const widget = dataWidgets[i];
    const nested = findEventValue(Array.isArray(widget?.value) ? widget.value : []);
    if (nested !== null) return nested;
  }
  return null;
}

// --- Platform Extraction ---
export function getPlatformFromRow(row = [], headers = []) {
  const platformIndex = headers.findIndex((header = {}) => {
    const label = String(header.display || header.uid || '').toLowerCase();
    return (
      label.includes('platform')
      || label.includes('found on')
      || label === 'source'
    );
  });
  if (platformIndex >= 0 && row[platformIndex]) {
    const cell = row[platformIndex];
    const value = cell?.display ?? cell?.value ?? cell?.api;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

export function getLanguageFromRow(row = [], headers = []) {
  const langIndex = headers.findIndex((header = {}) => {
    const label = String(header.display || header.uid || '').toLowerCase();
    return label === 'language' || label.includes('language');
  });
  if (langIndex >= 0 && row[langIndex]) {
    const cell = row[langIndex];
    const value = cell?.display ?? cell?.value ?? cell?.api;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

// --- Math/Number ---
export function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function hasPositiveNumber(value) {
  const parsed = toNumber(value);
  return parsed !== null && parsed > 0;
}

export function stableRandomFromString(value = '') {
  const input = String(value || 'seed');
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) % 1000000;
  }
  return (Math.abs(hash) % 1000) / 1000;
}

// --- Color/Chart ---
export function getPieColors(isDarkMode = false) {
  return isDarkMode
    ? ['#F94B4B', '#FE6464', '#F9B24B', '#62C150', '#38BDF8', '#818CF8', '#F472B6', '#FACC15']
    : ['#004FFE', '#038BFC', '#16A3B5', '#299914', '#F94B4B', '#F9B24B', '#818CF8', '#F472B6'];
}

export function getHighlightGradient({ page, isDarkMode }) {
  if (!isDarkMode) {
    return `linear-gradient(135deg, ${page.altHighlight} 0%, ${page.cardBg} 55%, ${page.highlight} 100%)`;
  }
  return `linear-gradient(135deg, ${page.highlight} -20%, ${page.altBg} 50%, ${page.highlight} 120%)`;
}

export function getHighlightBoxShadow({ isDarkMode }) {
  return isDarkMode
    ? '0 12px 40px #f94b4b3d'
    : '0 6px 15px #728af43d';
}

export const chartTooltip = {
  contentStyle: {
    background: '#0E2435',
    border: 'none',
    borderRadius: '10px',
    color: '#E9F4FF',
    fontSize: '12px',
  },
  labelStyle: { color: '#E9F4FF', fontWeight: 600 },
  itemStyle: { color: '#FFFFFF' },
  wrapperStyle: { color: '#FFFFFF' },
};

export function getProviderColor(idx, isDarkMode = false) {
  const colors = getPieColors(isDarkMode);
  return colors[idx % colors.length];
}

export function getCountryColor(idx, isDarkMode = false) {
  const colors = getPieColors(isDarkMode);
  return colors[idx % colors.length];
}

// --- Main Stats Calculations ---
export function buildTrackerStats(
  rows = [],
  totalCount = 0,
  hostMetricsRows = [],
  reviewHeaders = [],
  firstDetectedByDomainAll = {},
  visitorMultiplier = 0.2,
  useSubscriptionFee = ESTIMATE_SUBSCRIPTION_FEE,
  eventTitles = [],
) {
  const domainCounts = {};
  const firstDetectedByDomain = {};
  const uniqueUrls = new Set();

  rows.forEach((row) => {
    const url = extractPrimaryUrl(row);
    if (url) uniqueUrls.add(url);
    const domain = getDomainFromUrl(url);
    if (domain) domainCounts[domain] = (domainCounts[domain] || 0) + 1;
    if (domain) {
      const foundDateTime = getFoundDateTimeFromRow(row, reviewHeaders);
      if (foundDateTime !== null) {
        const currentFirstDetected = firstDetectedByDomain[domain];
        firstDetectedByDomain[domain] = currentFirstDetected
          ? Math.min(currentFirstDetected, foundDateTime)
          : foundDateTime;
      }
    }
  });

  const domainBreakdown = Object.entries(domainCounts)
    .map(([domain, count]) => ({
      domain,
      count,
      firstDetectedAt: firstDetectedByDomainAll[domain] || firstDetectedByDomain[domain] || null,
    }))
    .sort((a, b) => b.count - a.count);

  const totalInfringingLinks = totalCount || rows.length;
  const distinctUrlCount = uniqueUrls.size;
  const totalUniqueDomains = domainBreakdown.length;
  const totalEvents = Array.isArray(eventTitles) ? eventTitles.length : 1;
  const eventCounts = {};
  rows.forEach((row) => {
    const eventTitle = getEventTitleFromRow(row, reviewHeaders);
    eventCounts[eventTitle] = (eventCounts[eventTitle] || 0) + 1;
  });
  const eventBreakdown = Object.entries(eventCounts).map(([eventName, infringements]) => ({
    eventName,
    infringements,
  }));
  eventBreakdown.sort((a, b) => b.infringements - a.infringements);

  // Traffic
  const trafficMap = hostMetricsRows.reduce((acc, item) => {
    const host = normalizeDomain(item.host || item.domain || '');
    if (!host) return acc;
    acc[host] = { ...item, visitors: Math.ceil(toNumber(item.visitors) * visitorMultiplier) || 0 };
    return acc;
  }, {});

  const rawMonthlyVisitors = domainBreakdown
    .filter(({ domain }) => !nonTrafficDomains.includes(extractRootDomain(domain)))
    .map(({ domain }) => trafficMap[domain] || {})
    .map((metadata) => toNumber(metadata.visitors))
    .filter((value) => hasPositiveNumber(value));

  const visitorsAverage = rawMonthlyVisitors.length
    ? rawMonthlyVisitors.reduce((sum, value) => sum + value, 0) / rawMonthlyVisitors.length
    : null;

  const hydratedDomainStats = domainBreakdown.map((item) => {
    const metadata = trafficMap[item.domain] || {};
    const rawVisitorsValue = toNumber(metadata.visitors);
    const rawGlobalRankValue = toNumber(metadata.global_ranking);
    let estimatedMonthlyVisitors = rawVisitorsValue;
    if (!hasPositiveNumber(estimatedMonthlyVisitors)) {
      if (visitorsAverage) {
        estimatedMonthlyVisitors = Math.round(
          visitorsAverage + stableRandomFromString(`${item.domain}-monthly`) * 5000,
        );
      } else {
        estimatedMonthlyVisitors = 0;
      }
    }
    const estimatedGlobalRank = hasPositiveNumber(rawGlobalRankValue) ? rawGlobalRankValue : null;
    return {
      ...item,
      ipAddress: metadata.ip_address || metadata.ipAddress || null,
      ipGeolocation: metadata.ip_geolocation || metadata.ipGeolocation || DEFAULT_TERRITORY,
      ipTimestamp: metadata.ip_timestamp || metadata.ipTimestamp || null,
      ispGeolocation: metadata.internet_service_provider_geolocation
        || metadata.internetServiceProviderGeolocation || DEFAULT_TERRITORY,
      ispName: metadata.internet_service_provider_name || metadata.internetServiceProviderName
        || metadata.isp_name || metadata.ispName || DEFAULT_PROVIDER,
      monthlyVisitors: estimatedMonthlyVisitors,
      globalRank: estimatedGlobalRank,
    };
  });

  const trafficDomainStats = hydratedDomainStats.filter(
    (item) => !nonTrafficDomains.includes(extractRootDomain(item.domain)),
  );

  const hostingProviderStats = Object.values(
    hydratedDomainStats.reduce((acc, item) => {
      const key = item.ispName || DEFAULT_PROVIDER;
      if (!acc[key]) acc[key] = { provider: key, domains: 0, infringements: 0 };
      acc[key].domains += 1;
      acc[key].infringements += item.count;
      return acc;
    }, {}),
  ).sort((a, b) => b.infringements - a.infringements);

  const topTerritories = Object.values(
    hydratedDomainStats.reduce((acc, item) => {
      const key = item.ipGeolocation || DEFAULT_TERRITORY;
      if (!acc[key]) acc[key] = { territory: key, infringements: 0 };
      acc[key].infringements += item.count;
      return acc;
    }, {}),
  ).sort((a, b) => b.infringements - a.infringements).slice(0, 10);

  const topVisitedSites = trafficDomainStats
    .slice()
    .sort((a, b) => b.monthlyVisitors - a.monthlyVisitors)
    .slice(0, 10);

  const uniqueVisitorsCount = trafficDomainStats.reduce(
    (sum, item) => sum + Math.round((item.monthlyVisitors || 0) / 30),
    0,
  );

  const usVisitorsPercentage = 10 + Math.floor(stableRandomFromString(`${totalInfringingLinks}-us-visitors`) * 41);
  const usVisitorsEstimate = Math.round((uniqueVisitorsCount * usVisitorsPercentage) / 100);
  const averageUsVisitorsEstimate = totalUniqueDomains
    ? Math.round(usVisitorsEstimate / totalUniqueDomains)
    : 0;

  const estimatedLossRevenue = (uniqueVisitorsCount / 10) * useSubscriptionFee;

  const mirrorDomainFamilies = Object.values(
    hydratedDomainStats.reduce((acc, item) => {
      const rootDomain = extractRootDomain(item.domain);
      if (!acc[rootDomain]) {
        acc[rootDomain] = { rootDomain, domains: [], infringements: 0 };
      }
      acc[rootDomain].domains.push(item.domain);
      acc[rootDomain].infringements += item.count;
      return acc;
    }, {}),
  ).map((item) => ({
    ...item,
    mirrorDomains: item.domains.length,
  })).sort((a, b) => b.mirrorDomains - a.mirrorDomains);

  const mirrorDomainCount = mirrorDomainFamilies.reduce(
    (sum, item) => sum + item.mirrorDomains,
    0,
  );
  const rootDomainCount = mirrorDomainFamilies.length;
  const streamResurfacingRate = totalUniqueDomains
    ? Math.round((mirrorDomainCount / totalUniqueDomains) * 100)
    : 0;

  const viewerDomainEstimates = trafficDomainStats
    .map((item) => {
      const estimatedConcurrentViewers = Math.round((item.monthlyVisitors || 0) / 30);
      const estimatedConcurrentPerLink = Math.round(
        estimatedConcurrentViewers / Math.max(1, item.count),
      );
      return {
        ...item,
        linksStreams: item.count,
        estimatedConcurrentViewers,
        estimatedConcurrentPerLink,
      };
    })
    .sort((a, b) => b.estimatedConcurrentViewers - a.estimatedConcurrentViewers);

  const aggregateConcurrentEstimate = viewerDomainEstimates.reduce(
    (sum, item) => sum + item.estimatedConcurrentViewers,
    0,
  );
  const uniquePirateViewerEstimate = Math.round(aggregateConcurrentEstimate * 3.2);
  const modeledTotalEventAudience = Math.round(uniquePirateViewerEstimate * 1.15);

  const countryViewerDistribution = topTerritories
    .map((item) => ({
      territory: item.territory,
      infringements: item.infringements,
      estimatedViewers: Math.max(
        1,
        Math.round(
          (item.infringements / Math.max(1, totalInfringingLinks)) * uniqueVisitorsCount,
        ),
      ),
    }))
    .sort((a, b) => b.estimatedViewers - a.estimatedViewers);

  const usViewers = usVisitorsEstimate;
  const internationalViewers = Math.max(0, uniqueVisitorsCount - usVisitorsEstimate);
  const usIntlDistribution = [
    { segment: 'U.S.', viewers: usViewers },
    { segment: 'Other Countries', viewers: internationalViewers },
  ];

  const detectionByDomain = viewerDomainEstimates
    .map((item) => {
      const densityScore = Math.min(
        100,
        Math.round(item.linksStreams * 8 + (item.monthlyVisitors || 0) / 50000),
      );
      const avgMinutesToDetection = Math.max(
        4,
        Math.round(62 - densityScore * 0.42),
      );
      return {
        domain: item.domain,
        linksStreams: item.linksStreams,
        densityScore,
        avgMinutesToDetection,
        marketDensity: densityScore >= 70 ? 'High-density' : 'Standard',
      };
    })
    .sort((a, b) => a.avgMinutesToDetection - b.avgMinutesToDetection);

  const useTimeToDetection = detectionByDomain?.length && detectionByDomain
    .reduce((sum, item) => sum + item.avgMinutesToDetection, 0) / detectionByDomain.length;
  const avgTimeToDetection = useTimeToDetection ? Math.round(useTimeToDetection) : 0;
  const highDensityMarkets = detectionByDomain
    .filter((item) => item.marketDensity === 'High-density')
    .slice(0, 6);

  const languageCounts = {};
  rows.forEach((row) => {
    const lang = getLanguageFromRow(row, reviewHeaders);
    if (!lang) return;
    languageCounts[lang] = (languageCounts[lang] || 0) + 1;
  });
  const languageBreakdown = Object.entries(languageCounts)
    .map(([language, count]) => ({ language, count }))
    .sort((a, b) => b.count - a.count);

  return {
    totalInfringingLinks,
    distinctUrlCount,
    uniqueVisitorsCount,
    usVisitorsEstimate,
    averageUsVisitorsEstimate,
    usVisitorsPercentage,
    estimatedLossRevenue,
    totalUniqueDomains,
    totalEvents,
    googleUrls: Math.round(totalInfringingLinks * 0.82),
    bingUrls: Math.round(totalInfringingLinks * 0.18),
    bingPercentage: 18,
    eventBreakdown,
    domainBreakdown,
    hydratedDomainStats,
    hostingProviderStats,
    topTerritories,
    topVisitedSites,
    mirrorDomainFamilies,
    rootDomainCount,
    mirrorDomainCount,
    streamResurfacingRate,
    viewerDomainEstimates,
    aggregateConcurrentEstimate,
    uniquePirateViewerEstimate,
    modeledTotalEventAudience,
    countryViewerDistribution,
    usIntlDistribution,
    detectionByDomain,
    avgTimeToDetection,
    highDensityMarkets,
    languageBreakdown,
  };
}

// --- Merging ---
export function mergeDomainArray(aList = [], bList = []) {
  const map = {};
  [...aList, ...bList].forEach((item) => {
    if (!map[item.domain]) {
      map[item.domain] = { ...item };
    } else {
      map[item.domain] = {
        ...map[item.domain],
        count: map[item.domain].count + item.count,
        monthlyVisitors: Math.max(map[item.domain].monthlyVisitors || 0, item.monthlyVisitors || 0),
      };
    }
  });
  return Object.values(map).sort((x, y) => y.count - x.count);
}

export function mergeStats(a, b) {
  const hydratedDomainStats = mergeDomainArray(a.hydratedDomainStats, b.hydratedDomainStats);
  const totalInfringingLinks = a.totalInfringingLinks + b.totalInfringingLinks;
  const uniqueVisitorsCount = a.uniqueVisitorsCount + b.uniqueVisitorsCount;
  const usVisitorsEstimate = a.usVisitorsEstimate + b.usVisitorsEstimate;
  const totalUniqueDomains = hydratedDomainStats.length;
  const aggregateConcurrentEstimate = a.aggregateConcurrentEstimate + b.aggregateConcurrentEstimate;
  return {
    totalInfringingLinks,
    distinctUrlCount: a.distinctUrlCount + b.distinctUrlCount,
    uniqueVisitorsCount,
    usVisitorsEstimate,
    averageUsVisitorsEstimate: totalUniqueDomains
      ? Math.round(usVisitorsEstimate / totalUniqueDomains)
      : 0,
    usVisitorsPercentage: Math.round((a.usVisitorsPercentage + b.usVisitorsPercentage) / 2),
    estimatedLossRevenue: a.estimatedLossRevenue + b.estimatedLossRevenue,
    totalUniqueDomains,
    totalEvents: a.totalEvents + b.totalEvents,
    googleUrls: a.googleUrls + b.googleUrls,
    bingUrls: a.bingUrls + b.bingUrls,
    bingPercentage: Math.round((a.bingPercentage + b.bingPercentage) / 2),
    eventBreakdown: Object.values(
      [...a.eventBreakdown, ...b.eventBreakdown].reduce((acc, curr) => {
        if (!acc[curr.eventName]) {
          acc[curr.eventName] = { ...curr };
        } else {
          acc[curr.eventName].infringements += curr.infringements;
        }
        return acc;
      }, {}),
    ).sort((x, y) => y.infringements - x.infringements),
    domainBreakdown: mergeDomainArray(a.domainBreakdown, b.domainBreakdown),
    hydratedDomainStats,
    hostingProviderStats: a.hostingProviderStats,
    topTerritories: a.topTerritories,
    topVisitedSites: [...a.topVisitedSites, ...b.topVisitedSites]
      .sort((x, y) => y.monthlyVisitors - x.monthlyVisitors)
      .slice(0, 10),
    mirrorDomainFamilies: a.mirrorDomainFamilies,
    mirrorDomainCount: a.mirrorDomainCount + b.mirrorDomainCount,
    streamResurfacingRate: Math.round((a.streamResurfacingRate + b.streamResurfacingRate) / 2),
    viewerDomainEstimates: mergeDomainArray(a.viewerDomainEstimates, b.viewerDomainEstimates),
    aggregateConcurrentEstimate,
    uniquePirateViewerEstimate: Math.round(aggregateConcurrentEstimate * 3.2),
    modeledTotalEventAudience: Math.round(aggregateConcurrentEstimate * 3.2 * 1.15),
    countryViewerDistribution: a.countryViewerDistribution,
    usIntlDistribution: [
      { segment: 'U.S.', viewers: a.usIntlDistribution[0]?.viewers + b.usIntlDistribution[0]?.viewers },
      { segment: 'Other Countries', viewers: a.usIntlDistribution[1]?.viewers + b.usIntlDistribution[1]?.viewers },
    ],
    detectionByDomain: a.detectionByDomain,
    avgTimeToDetection: Math.round((a.avgTimeToDetection + b.avgTimeToDetection) / 2),
    highDensityMarkets: a.highDensityMarkets,
    languageBreakdown: Object.values(
      [...(a.languageBreakdown || []), ...(b.languageBreakdown || [])].reduce((acc, item) => {
        if (!acc[item.language]) acc[item.language] = { language: item.language, count: 0 };
        acc[item.language].count += item.count;
        return acc;
      }, {}),
    ).sort((x, y) => y.count - x.count),
  };
}

export function getKpiAndExecutiveSummary({
  trackerStats,
  preEventStats,
  eventDayStats,
  utils,
}) {
  // KPI stats
  const kpiStats = trackerStats;
  const kpiGooglePercentage = kpiStats.totalInfringingLinks
    ? Math.round((kpiStats.googleUrls / kpiStats.totalInfringingLinks) * 100)
    : 0;

  // Spike calculations
  /* eslint-disable no-nested-ternary, indent, operator-linebreak */
  const urlSpikePct = preEventStats.totalInfringingLinks
    ? Math.abs(
        Math.round(
          ((eventDayStats.totalInfringingLinks - preEventStats.totalInfringingLinks) /
            preEventStats.totalInfringingLinks) *
            100,
        ),
      )
    : eventDayStats.totalInfringingLinks
      ? 100
      : 0;

  const trafficSpikePct = preEventStats.uniqueVisitorsCount
    ? Math.abs(
        Math.round(
          ((eventDayStats.uniqueVisitorsCount - preEventStats.uniqueVisitorsCount) /
            preEventStats.uniqueVisitorsCount) *
            100,
        ),
      )
    : eventDayStats.uniqueVisitorsCount
      ? 100
      : 0;

  const domainsSpikePct = preEventStats.totalUniqueDomains
    ? Math.abs(
        Math.round(
          ((eventDayStats.totalUniqueDomains - preEventStats.totalUniqueDomains) /
            preEventStats.totalUniqueDomains) *
            100,
        ),
      )
    : eventDayStats.totalUniqueDomains
      ? 100
      : 0;
  /* eslint-enable no-nested-ternary, indent, operator-linebreak */

  // Executive summary
  const providerTrafficData = Object.values(
    kpiStats.hydratedDomainStats
      .filter((item) => !utils.nonTrafficDomains.includes(utils.extractRootDomain(item.domain)))
      .reduce((acc, item) => {
        const provider = item.ispName || utils.DEFAULT_PROVIDER;
        if (!acc[provider]) acc[provider] = { provider, traffic: 0 };
        acc[provider].traffic += item.monthlyVisitors || 0;
        return acc;
      }, {}),
  ).sort((a, b) => b.traffic - a.traffic);

  const totalProviderTraffic = providerTrafficData.reduce((sum, item) => sum + item.traffic, 0);
  const top3ProviderTraffic = providerTrafficData
    .slice(0, 3)
    .reduce((sum, item) => sum + item.traffic, 0);
  const top3ProviderTrafficPct = totalProviderTraffic
    ? Math.round((top3ProviderTraffic / totalProviderTraffic) * 100)
    : 0;

  const topDomainsPostEvent = kpiStats.hydratedDomainStats
    .map((item) => ({
      ...item,
      infrastructureRepeatRate: Math.round(Math.random() * 100),
      score: item.count + (item.monthlyVisitors || 0) / 1000,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 10);

  const piracyConcentration = kpiStats.topTerritories
    .slice(0, 3)
    .map((item) => item.territory)
    .join(', ');

  const leverageProviders = providerTrafficData
    .slice(0, 2)
    .map((item) => item.provider)
    .join(', ');

  const chokePointDomains = topDomainsPostEvent
    .filter((item) => item.infrastructureRepeatRate >= 25)
    .slice(0, 4)
    .map((item) => item.domain)
    .join(', ');

  return {
    kpiStats,
    kpiGooglePercentage,
    urlSpikePct,
    trafficSpikePct,
    domainsSpikePct,
    providerTrafficData,
    totalProviderTraffic,
    top3ProviderTraffic,
    top3ProviderTrafficPct,
    topDomainsPostEvent,
    piracyConcentration,
    leverageProviders,
    chokePointDomains,
  };
}

// --- Helpers to be used for memoization inside the page component ---
export function getEventTitleOptions(reviewRows = [], reviewHeaders = []) {
  const titles = (reviewRows || [])
    .map((row) => {
      const titleIdx = reviewHeaders.findIndex(
        (header) => String(header.display || header.uid || '').toLowerCase().includes('title'),
      );
      if (titleIdx !== -1 && row[titleIdx]?.display) {
        return row[titleIdx].display;
      }
      return null;
    })
    .filter(Boolean);

  return Array.from(new Set(titles)).map((title) => ({
    label: title,
    value: title,
  }));
}

export function classifyRowsByEvent(
  reviewRows = [], reviewHeaders = [], eventDateTimestamp = null,
) {
  return reviewRows.reduce(
    (acc, row) => {
      const current = { ...acc };
      const foundDate = getFoundDateFromRow(row, reviewHeaders);
      if (foundDate === null || eventDateTimestamp === null) {
        current.eventDay.push(row);
        return current;
      }
      if (foundDate < eventDateTimestamp) {
        current.preEvent.push(row);
      } else {
        // Everything on or after event date is eventDay
        current.eventDay.push(row);
      }
      return current;
    },
    { preEvent: [], eventDay: [], postEvent: [] },
  );
}

export function getAllFirstDetectedByDomain(reviewRows, reviewHeaders) {
  return reviewRows.reduce((acc, row) => {
    const current = { ...acc };
    const domain = getDomainFromUrl(extractPrimaryUrl(row));
    const foundDateTime = getFoundDateTimeFromRow(row, reviewHeaders);
    if (!domain || foundDateTime === null) return current;
    current[domain] = current[domain]
      ? Math.min(current[domain], foundDateTime)
      : foundDateTime;
    return current;
  }, {});
}

export function getProviderBarData(hostingProviderStats, shortLabelFn) {
  return hostingProviderStats
    .slice(0, 8)
    .map((item) => ({
      provider: shortLabelFn(item.provider, 16),
      fullProvider: item.provider,
      infringements: item.infringements,
      domains: item.domains,
    }));
}

export function getTrafficSeriesData(topVisitedSites, shortLabelFn) {
  return topVisitedSites.map((item) => ({
    domain: shortLabelFn(item.domain, 14),
    fullDomain: item.domain,
    monthlyVisitors: Math.round((item.monthlyVisitors || 0) / 1000),
    infringements: item.count,
    globalRank: item.globalRank,
  }));
}

export function getGeographyData(countryViewerDistribution, shortLabelFn) {
  return countryViewerDistribution
    .slice(0, 10)
    .map((item) => ({
      territory: shortLabelFn(item.territory, 15),
      fullTerritory: item.territory,
      estimatedViewers: item.estimatedViewers,
    }));
}

export function getDetectionChartData(detectionByDomain, shortLabelFn) {
  return detectionByDomain
    .slice(0, 10)
    .map((item) => ({
      domain: shortLabelFn(item.domain, 14),
      fullDomain: item.domain,
      avgMinutesToDetection: item.avgMinutesToDetection,
    }));
}

export function getTopSitesByInfringementsData(domainBreakdown, shortLabelFn) {
  return domainBreakdown
    .slice(0, 12)
    .map((item) => ({
      domain: shortLabelFn(item.domain, 15),
      fullDomain: item.domain,
      infringements: item.count,
    }));
}

export function getPostEventAllPlatformBreakdownData(
  preAndEventRows, reviewHeaders, shortLabelFn, getPlatformFromRowFn,
) {
  const platformCounts = {};
  preAndEventRows.forEach((row) => {
    const platform = getPlatformFromRowFn(row, reviewHeaders);
    if (!platform) return;
    platformCounts[platform] = (platformCounts[platform] || 0) + 1;
  });
  return Object.entries(platformCounts)
    .map(([platform, count]) => ({
      platform: shortLabelFn(platform, 18),
      fullPlatform: platform,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

export function getSocialPlatformBreakdownData(
  activeRows, reviewHeaders, shortLabelFn, getPlatformFromRowFn,
) {
  const platformCounts = {};
  activeRows.forEach((row) => {
    const platform = getPlatformFromRowFn(row, reviewHeaders);
    if (!platform || platform.toLowerCase() === 'internet') return;
    platformCounts[platform] = (platformCounts[platform] || 0) + 1;
  });
  return Object.entries(platformCounts)
    .map(([platform, count]) => ({
      platform: shortLabelFn(platform, 18),
      fullPlatform: platform,
      count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
}

// --- Watermark Grouping ---
export function getWatermarkValueFromRow(row = [], key = '') {
  if (!key) return null;

  if (Array.isArray(row)) {
    return row.find((cell) => cell?.display?.[key])?.display?.[key] || null;
  }

  return null;
}

export function hasWatermarkInfo(rows = [], watermarkGroupingKey = '') {
  return rows.some(
    (row) => Array.isArray(row) && row.some((cell) => cell && cell.display?.[watermarkGroupingKey]),
  );
}

export function buildWatermarkGroupStats(rows = [], key = '') {
  const groups = {};
  rows.forEach((row) => {
    const value = getWatermarkValueFromRow(row, key);
    if (!value) return;

    if (!groups[value]) {
      groups[value] = { key: value, urls: new Set(), infringements: 0 };
    }

    groups[value].infringements += 1;

    const url = extractPrimaryUrl(row);
    if (url) groups[value].urls.add(url);
  });

  return Object.values(groups)
    .map((item) => ({
      key: item.key,
      urls: item.urls.size,
      infringements: item.infringements,
    }))
    .sort((a, b) => b.infringements - a.infringements);
}

export function getWatermarkValuesByDomain(rows = [], key = '') {
  const domainWatermarks = {};

  rows.forEach((row) => {
    const url = extractPrimaryUrl(row);
    const domain = getDomainFromUrl(url);
    const value = getWatermarkValueFromRow(row, key);

    if (!domain || !value) return;

    if (!domainWatermarks[domain]) domainWatermarks[domain] = new Set();

    domainWatermarks[domain].add(value);
  });

  Object.keys(domainWatermarks).forEach((domain) => {
    domainWatermarks[domain] = Array.from(domainWatermarks[domain]);
  });

  return domainWatermarks;
}

export function camelToLabel(text) {
  if (!text) return '';

  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

export function buildWatermarkLinkRows(rows = [], key = '') {
  const result = [];

  rows.forEach((row) => {
    const value = getWatermarkValueFromRow(row, key);
    if (!value) return;

    const url = extractPrimaryUrl(row);
    const domain = getDomainFromUrl(url);

    result.push({ domain: domain || '—', url: url || '—', watermarkValue: value });
  });

  return result;
}

export function getArticle(word) {
  if (!word) return 'a';
  return /^[aeiou]/i.test(word.trim()) ? 'an' : 'a';
}

// --- Management Report ---
export function buildManagementReport(rows = [], headers = [], affiliateKey = null) {
  const eventMap = {};

  rows.forEach((row) => {
    const url = extractPrimaryUrl(row);
    const domain = getDomainFromUrl(url);
    const affiliateName = affiliateKey
      ? (getWatermarkValueFromRow(row, affiliateKey) || null)
      : null;
    const matchday = getMatchdayFromRow(row, headers);

    if (!eventMap[matchday]) {
      eventMap[matchday] = {
        totalInfringements: 0,
        allDomains: new Set(),
        affiliates: {},
        unattributedInfringements: 0,
        unattributedDomains: new Set(),
        matchday,
      };
    }

    const entry = eventMap[matchday];
    entry.totalInfringements += 1;
    if (domain) entry.allDomains.add(domain);

    if (affiliateName) {
      if (!entry.affiliates[affiliateName]) {
        entry.affiliates[affiliateName] = {
          name: affiliateName,
          infringements: 0,
          domains: new Set(),
        };
      }
      entry.affiliates[affiliateName].infringements += 1;
      if (domain) entry.affiliates[affiliateName].domains.add(domain);
    } else {
      entry.unattributedInfringements += 1;
      if (domain) entry.unattributedDomains.add(domain);
    }
  });

  return Object.values(eventMap)
    .map((entry) => {
      const affiliateList = Object.values(entry.affiliates)
        .map((aff) => ({
          name: aff.name,
          infringements: aff.infringements,
          uniqueDomains: aff.domains.size,
        }))
        .sort((a, b) => b.infringements - a.infringements);

      return {
        totalInfringements: entry.totalInfringements,
        totalUniqueDomains: entry.allDomains.size,
        affiliatesIdentified: affiliateList.length,
        affiliates: affiliateList,
        unattributedInfringements: entry.unattributedInfringements,
        unattributedDomains: entry.unattributedDomains.size,
        matchday: entry.matchday,
      };
    })
    .sort((a, b) => b.totalInfringements - a.totalInfringements);
}
